/**
 * Geocoding via Nominatim (OpenStreetMap, free, no API key).
 *
 * Rate limit ufficiale: max 1 req/sec. Per batch geocoding usare il queue
 * helper sotto. Nominatim richiede un User-Agent identificativo (fair-use).
 *
 * Docs: https://nominatim.org/release-docs/develop/api/Search/
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const USER_AGENT = 'DryConfig/1.0 (https://dryconfig.com)';

export interface GeocodeAddress {
  addressLine?: string | null;
  city?: string | null;
  zipCode?: string | null;
  province?: string | null;
  countryCode?: string | null;
}

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  displayName: string;
}

function buildQuery(addr: GeocodeAddress): string {
  const parts = [
    addr.addressLine?.trim(),
    addr.zipCode?.trim(),
    addr.city?.trim(),
    addr.province?.trim(),
    addr.countryCode?.trim() || 'Italia',
  ].filter(Boolean);
  return parts.join(', ');
}

export async function geocodeAddress(addr: GeocodeAddress): Promise<GeocodeResult | null> {
  const q = buildQuery(addr);
  if (!q || q.length < 4) return null;

  const url = new URL(NOMINATIM_URL);
  url.searchParams.set('q', q);
  url.searchParams.set('format', 'json');
  url.searchParams.set('limit', '1');
  url.searchParams.set('addressdetails', '0');

  try {
    const res = await fetch(url.toString(), {
      headers: { 'User-Agent': USER_AGENT, Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Array<{ lat: string; lon: string; display_name: string }>;
    if (!data || data.length === 0) return null;
    const hit = data[0];
    const lat = Number(hit.lat);
    const lon = Number(hit.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { latitude: lat, longitude: lon, displayName: hit.display_name };
  } catch {
    return null;
  }
}

/**
 * Geocodifica una lista di indirizzi rispettando il rate limit di Nominatim
 * (1 req/s + un piccolo margine). Restituisce risultati nello stesso ordine,
 * con `null` per chi non è stato risolto.
 */
export async function geocodeBatch<T extends GeocodeAddress>(
  items: T[],
  onProgress?: (done: number, total: number) => void,
): Promise<Array<GeocodeResult | null>> {
  const results: Array<GeocodeResult | null> = [];
  for (let i = 0; i < items.length; i++) {
    const r = await geocodeAddress(items[i]);
    results.push(r);
    onProgress?.(i + 1, items.length);
    // Rate limit: 1.1s di sicurezza tra una richiesta e la successiva
    if (i < items.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 1100));
    }
  }
  return results;
}
